"use client";

import { useMemo } from "react";
import type { NormalisedHour } from "@/lib/conditions/daily-obs-types";
import type { SportDetail } from "@/lib/conditions/detail";
import type { ScoreExplanation } from "@/lib/conditions/factor-explanations";
import type { DivingEnhancements } from "@/lib/conditions/unified-types";
import type { TideData } from "@/lib/conditions/tide-format";
import { clampScore, type RadarDatum } from "./ConditionsRadar";
import { FactorFirst } from "./FactorFirst";
import CollapsibleSection from "@/app/_components/conditions/CollapsibleSection";
import { TideChart } from "@/app/_components/conditions/TideChart";

type Props = {
  hourlyData: NormalisedHour[];
  divingDetail: SportDetail;
  latitude?: number | null;
  enhancements?: DivingEnhancements | null;
  tide?: TideData | null;
  tidePreference?: string | null;
  explanation?: ScoreExplanation | null;
};

/**
 * The boat dive card's colours. Only a green reading is styled as a go: revision 2's grey "Boat
 * dives: Data unavailable" (assessBoatFeasibilityV2 without the sea state or gusts) takes the
 * neutral card, like the surge card's unavailable reading.
 */
export function boatFeasibilityCardClass(colour: string): string {
  switch (colour) {
    case "red": return "bg-red-50 border-red-200";
    case "amber": return "bg-amber-50 border-amber-200";
    case "green": return "bg-emerald-50 border-emerald-200";
    default: return "bg-warm-bg border-border-light";
  }
}

type DivingRadarDetail = Pick<
  SportDetail,
  "currentVelocityMs" | "secchiDepthM" | "chlorophyllMgM3"
>;

export function buildDivingRadarScores(
  hours: NormalisedHour[],
  divingDetail: DivingRadarDetail
): RadarDatum[] {
  const secchi = divingDetail.secchiDepthM ?? null;
  const visScore =
    secchi === null
      ? 7
      : secchi >= 15
        ? 10
        : secchi >= 8
          ? 8 + 2 * ((secchi - 8) / 7)
          : secchi >= 3
            ? 3 + 5 * ((secchi - 3) / 5)
            : (secchi / 3) * 3;

  const currentMs = divingDetail.currentVelocityMs ?? null;
  const currentScore =
    currentMs === null
      ? 7
      : currentMs <= 0.15
        ? 10
        : currentMs <= 0.4
          ? 10 - 4 * ((currentMs - 0.15) / 0.25)
          : currentMs <= 0.8
            ? 6 - 4 * ((currentMs - 0.4) / 0.4)
            : Math.max(0, 2 - (currentMs - 0.8) * 4);

  const daylightHours = hours.filter((h) => h.weather.is_day);
  const src = daylightHours.length > 0 ? daylightHours : hours;
  const stormHours = src.filter(
    (h) => (h.weather.precip_probability_pct ?? 0) > 70
  ).length;
  const stormRatio = src.length > 0 ? stormHours / src.length : 0;
  const weatherScore = 10 * (1 - stormRatio * 2.5);

  const chl = divingDetail.chlorophyllMgM3 ?? null;
  const waterQualityScore =
    chl === null
      ? 7
      : chl <= 0.5
        ? 10
        : chl <= 2
          ? 10 - 2 * ((chl - 0.5) / 1.5)
          : chl <= 5
            ? 8 - 3 * ((chl - 2) / 3)
            : chl <= 15
              ? 5 - 4 * ((chl - 5) / 10)
              : Math.max(0, 1 - (chl - 15) * 0.1);

  return [
    { axis: "Visibility", value: clampScore(visScore) },
    { axis: "Current", value: clampScore(currentScore) },
    { axis: "Weather", value: clampScore(weatherScore) },
    { axis: "Water quality", value: clampScore(waterQualityScore) },
  ];
}

export function getDivingRadarAssessment(radarScores: RadarDatum[]): string {
  const avg =
    radarScores.length > 0
      ? radarScores.reduce((s, r) => s + r.value, 0) / radarScores.length
      : 0;
  const vis = radarScores.find((r) => r.axis === "Visibility")?.value ?? 0;
  const current = radarScores.find((r) => r.axis === "Current")?.value ?? 0;
  if (avg >= 8 && vis >= 7) return "Outstanding dive conditions";
  if (avg >= 6) return "Good conditions for diving";
  if (vis < 3) return "Very poor visibility, consider postponing";
  if (current < 3) return "Strong current, advanced divers only";
  if (avg >= 4) return "Moderate conditions, plan accordingly";
  return "Challenging conditions for diving";
}

function VisibilityGauge({ secchiDepthM }: { secchiDepthM: number | null }) {
  if (secchiDepthM === null) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="text-[13px] text-text-muted">No satellite data</div>
        <div className="text-[11px] text-text-muted/70 mt-1">
          Secchi depth unavailable for this location
        </div>
      </div>
    );
  }

  const maxDepth = 25;
  const clampedDepth = Math.min(secchiDepthM, maxDepth);
  const fillPct = (clampedDepth / maxDepth) * 100;

  const qualityLabel =
    secchiDepthM >= 15
      ? "Excellent"
      : secchiDepthM >= 8
        ? "Good"
        : secchiDepthM >= 3
          ? "Moderate"
          : "Poor";

  const qualityColor =
    secchiDepthM >= 15
      ? "#059669"
      : secchiDepthM >= 8
        ? "#0d9488"
        : secchiDepthM >= 3
          ? "#d97706"
          : "#f87171";

  const zones = [
    { from: 0, to: 12, label: "Poor", y: 88 },
    { from: 12, to: 32, label: "Moderate", y: 68 },
    { from: 32, to: 60, label: "Good", y: 46 },
    { from: 60, to: 100, label: "Excellent", y: 20 },
  ];

  return (
    <div className="flex flex-col items-center">
      <svg
        width="60"
        height="240"
        viewBox="0 0 60 240"
        aria-label={`Underwater visibility: ${secchiDepthM.toFixed(1)}m, ${qualityLabel}`}
        role="img"
        className="block"
      >
        <defs>
          <linearGradient id="vis-gradient" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#78350f" stopOpacity="0.3" />
            <stop offset="30%" stopColor="#92400e" stopOpacity="0.2" />
            <stop offset="60%" stopColor="#0d9488" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.35" />
          </linearGradient>
        </defs>

        <rect
          x="10"
          y="10"
          width="40"
          height="220"
          rx="6"
          fill="url(#vis-gradient)"
          stroke="#e2e8f0"
          strokeWidth="1"
        />

        {zones.map((z) => (
          <text
            key={z.label}
            x="56"
            y={10 + (220 * (100 - (z.from + z.to) / 2)) / 100}
            fontSize="8"
            fill="#7A7A7A"
            textAnchor="end"
            dominantBaseline="middle"
            className="select-none"
          >
            {z.label}
          </text>
        ))}

        <text x="4" y="232" fontSize="8" fill="#7A7A7A" textAnchor="start" className="select-none">0m</text>
        <text x="4" y="16" fontSize="8" fill="#7A7A7A" textAnchor="start" className="select-none">25m</text>

        <line
          x1="8"
          y1={10 + 220 * (1 - fillPct / 100)}
          x2="52"
          y2={10 + 220 * (1 - fillPct / 100)}
          stroke={qualityColor}
          strokeWidth="3"
          strokeLinecap="round"
        />

        <circle
          cx="30"
          cy={10 + 220 * (1 - fillPct / 100)}
          r="5"
          fill={qualityColor}
          stroke="white"
          strokeWidth="2"
        />
      </svg>

      <div className="text-center mt-3">
        <div className="font-serif text-[22px] md:text-[26px] font-bold text-navy tabular-nums">
          {secchiDepthM.toFixed(1)}m
        </div>
        <div
          className="text-[12px] font-semibold mt-0.5"
          style={{ color: qualityColor }}
        >
          {qualityLabel}
        </div>
      </div>
    </div>
  );
}

type SafetyIndicator = {
  label: string;
  status: "good" | "moderate" | "poor";
  detail: string;
};

function SafetyCard({ indicator }: { indicator: SafetyIndicator }) {
  const dotColor =
    indicator.status === "good"
      ? "bg-emerald-500"
      : indicator.status === "moderate"
        ? "bg-amber-500"
        : "bg-red-400";

  return (
    <div className="bg-warm-bg rounded-[12px] p-3.5">
      <div className="flex items-center gap-2.5">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotColor}`} />
        <div className="min-w-0">
          <div className="text-[12px] font-semibold text-navy">{indicator.label}</div>
          <div className="text-[11px] text-text-muted">{indicator.detail}</div>
        </div>
      </div>
    </div>
  );
}

function WaterTemperatureCard({
  waterTempC,
  wetsuitRecommendation,
}: {
  waterTempC: number | null;
  wetsuitRecommendation: string | null;
}) {
  const tempColor =
    waterTempC === null
      ? "#7A7A7A"
      : waterTempC < 16
        ? "#3b82f6"
        : waterTempC < 22
          ? "#0d9488"
          : waterTempC < 28
            ? "#059669"
            : "#d97706";

  const tempLabel =
    waterTempC === null
      ? "N/A"
      : waterTempC < 16
        ? "Cold"
        : waterTempC < 22
          ? "Cool"
          : waterTempC < 28
            ? "Warm"
            : "Very warm";

  return (
    <div className="bg-warm-bg rounded-[12px] p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted mb-2">
        Water temperature
      </div>
      <div className="flex items-baseline gap-2">
        <span
          className="font-serif text-[22px] md:text-[26px] font-bold tabular-nums"
          style={{ color: tempColor }}
        >
          {waterTempC !== null ? `${waterTempC.toFixed(0)}°C` : "N/A"}
        </span>
        <span className="text-[12px] text-text-muted">{tempLabel}</span>
      </div>
      {wetsuitRecommendation && (
        <div className="text-[12px] text-text-muted mt-1.5">
          {wetsuitRecommendation}
        </div>
      )}
    </div>
  );
}

function ThermoclineEstimate({ waterTempC, latitude }: { waterTempC: number | null; latitude: number | null }) {
  const estimate = useMemo(() => {
    if (waterTempC === null) return null;

    const month = new Date().getMonth(); // 0-11
    const absLat = Math.abs(latitude ?? 45);

    // Seasonal thermocline model:
    // - Thermocline is most pronounced in summer when surface warms but deep water stays cold
    // - Tropical (< 25): weak seasonal thermocline, permanent thermocline deeper (50-200m)
    // - Temperate (25-50): strong seasonal thermocline in summer (10-30m depth)
    // - High latitude (> 50): weak or absent, well-mixed

    // Summer months (hemisphere-aware)
    const isSummerNH = month >= 4 && month <= 9; // May-Oct
    const isSummerSH = month >= 10 || month <= 3; // Nov-Apr
    const isSummer = latitude !== null ? (latitude >= 0 ? isSummerNH : isSummerSH) : isSummerNH;

    let thermoclineDepthM: number;
    let tempDropC: number;
    let strength: "strong" | "moderate" | "weak" | "absent";

    if (absLat < 25) {
      // Tropical: permanent thermocline, surface stays warm
      thermoclineDepthM = isSummer ? 30 : 40;
      tempDropC = isSummer ? 4 : 3;
      strength = waterTempC > 26 ? "moderate" : "weak";
    } else if (absLat < 50) {
      // Temperate: strong seasonal thermocline in summer
      if (isSummer && waterTempC > 16) {
        thermoclineDepthM = 10 + (waterTempC - 16) * 0.8; // Warmer surface = deeper thermocline
        tempDropC = Math.min(waterTempC - 10, 12); // Can't drop below ~8-10C typically
        strength = waterTempC > 20 ? "strong" : "moderate";
      } else {
        // Winter: well-mixed, minimal thermocline
        thermoclineDepthM = 50;
        tempDropC = 1;
        strength = "absent";
      }
    } else {
      // High latitude: generally well-mixed
      thermoclineDepthM = 50;
      tempDropC = isSummer ? 2 : 0;
      strength = isSummer && waterTempC > 12 ? "weak" : "absent";
    }

    const deepTempC = waterTempC - tempDropC;

    let suitNote: string;
    if (deepTempC < 10) suitNote = "Consider 7mm or drysuit for depth";
    else if (deepTempC < 14) suitNote = "5mm suit recommended at depth";
    else if (deepTempC < 18) suitNote = "3mm+ at depth, shorty insufficient";
    else if (deepTempC < 22) suitNote = "Shorty adequate at depth";
    else suitNote = "No additional suit needed at depth";

    return { thermoclineDepthM: Math.round(thermoclineDepthM), tempDropC, deepTempC, strength, suitNote };
  }, [waterTempC, latitude]);

  if (!estimate || estimate.strength === "absent") return null;

  const strengthColour = estimate.strength === "strong" ? "#3b82f6" : estimate.strength === "moderate" ? "#0d9488" : "#9ca3af";

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
      <div className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-4">
        Thermocline estimate
      </div>

      {/* Visual depth indicator */}
      <div className="flex items-start gap-4">
        <div className="w-[60px] shrink-0">
          <svg viewBox="0 0 60 140" className="w-full h-auto" role="img" aria-label="Thermocline depth diagram">
            {/* Surface warm layer */}
            <rect x="10" y="10" width="40" height={Math.min(estimate.thermoclineDepthM * 2, 80)} rx="4" fill="#0d9488" opacity="0.2" />
            {/* Thermocline line */}
            <line
              x1="8" y1={10 + Math.min(estimate.thermoclineDepthM * 2, 80)}
              x2="52" y2={10 + Math.min(estimate.thermoclineDepthM * 2, 80)}
              stroke={strengthColour}
              strokeWidth="2"
              strokeDasharray="4 2"
            />
            {/* Deep cold layer */}
            <rect
              x="10" y={10 + Math.min(estimate.thermoclineDepthM * 2, 80)}
              width="40" height={130 - Math.min(estimate.thermoclineDepthM * 2, 80)}
              rx="4" fill="#3b82f6" opacity="0.15"
            />
            {/* Temp labels */}
            <text x="30" y={Math.min(estimate.thermoclineDepthM, 40) + 5} textAnchor="middle" className="fill-navy text-[9px] font-semibold">
              {waterTempC!.toFixed(0)}°C
            </text>
            <text x="30" y={Math.min(estimate.thermoclineDepthM * 2, 80) + 30} textAnchor="middle" className="fill-blue-600 text-[9px] font-semibold">
              ~{estimate.deepTempC.toFixed(0)}°C
            </text>
            {/* Depth label at thermocline */}
            <text x="55" y={10 + Math.min(estimate.thermoclineDepthM * 2, 80) + 4} textAnchor="end" className="fill-text-muted text-[8px]">
              ~{estimate.thermoclineDepthM}m
            </text>
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ backgroundColor: strengthColour + "18", color: strengthColour, border: `1px solid ${strengthColour}40` }}
            >
              {estimate.strength === "strong" ? "Strong thermocline" : estimate.strength === "moderate" ? "Moderate thermocline" : "Weak thermocline"}
            </span>
          </div>

          <div className="space-y-2 text-[13px]">
            <div className="flex justify-between">
              <span className="text-text-muted">Estimated depth</span>
              <span className="font-semibold text-navy tabular-nums">~{estimate.thermoclineDepthM}m</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Temperature drop</span>
              <span className="font-semibold text-navy tabular-nums">{"−"}{estimate.tempDropC.toFixed(0)}°C</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Deep temperature</span>
              <span className="font-semibold tabular-nums" style={{ color: "#3b82f6" }}>~{estimate.deepTempC.toFixed(0)}°C</span>
            </div>
          </div>

          <p className="text-[11px] text-text-muted mt-3 leading-[1.4]">{estimate.suitNote}</p>
        </div>
      </div>

      <p className="text-[10px] text-text-muted/70 mt-3 italic leading-[1.4]">
        Seasonal estimate based on surface temperature and latitude. Actual conditions vary by site.
      </p>
    </div>
  );
}

export function DivingInsights({ hourlyData, divingDetail, latitude, enhancements, tide, tidePreference, explanation }: Props) {
  const hours = useMemo(() => hourlyData ?? [], [hourlyData]);

  const safetyIndicators = useMemo<SafetyIndicator[]>(() => {
    const indicators: SafetyIndicator[] = [];

    const waveH = divingDetail.waveHeightM;
    indicators.push({
      label: "Waves",
      status: waveH < 1 ? "good" : waveH < 2 ? "moderate" : "poor",
      detail:
        waveH < 1
          ? `${waveH.toFixed(1)}m - calm surface`
          : waveH < 2
            ? `${waveH.toFixed(1)}m - moderate swell`
            : `${waveH.toFixed(1)}m - rough surface`,
    });

    const currentMs = divingDetail.currentVelocityMs ?? null;
    indicators.push({
      label: "Current",
      status:
        currentMs === null
          ? "moderate"
          : currentMs < 0.3
            ? "good"
            : currentMs < 0.6
              ? "moderate"
              : "poor",
      detail:
        currentMs === null
          ? "No current data"
          : currentMs < 0.3
            ? `${currentMs.toFixed(2)} m/s - mild`
            : currentMs < 0.6
              ? `${currentMs.toFixed(2)} m/s - noticeable drift`
              : `${currentMs.toFixed(1)} m/s - strong drift`,
    });

    const windKmh = divingDetail.windSpeedKmh;
    indicators.push({
      label: "Wind",
      status: windKmh < 15 ? "good" : windKmh < 30 ? "moderate" : "poor",
      detail:
        windKmh < 15
          ? `${Math.round(windKmh)} km/h - light`
          : windKmh < 30
            ? `${Math.round(windKmh)} km/h - moderate`
            : `${Math.round(windKmh)} km/h - strong`,
    });

    const stormHours = hours.filter(
      (h) => (h.weather.precip_probability_pct ?? 0) > 70
    );
    const stormRisk = stormHours.length;
    indicators.push({
      label: "Storm risk",
      status: stormRisk === 0 ? "good" : stormRisk <= 3 ? "moderate" : "poor",
      detail:
        stormRisk === 0
          ? "No storm risk today"
          : stormRisk <= 3
            ? `${stormRisk}h with high precip probability`
            : `${stormRisk}h with storm conditions`,
    });

    const visValues = hours
      .map((h) => h.weather.visibility_m)
      .filter((v): v is number => v != null);
    if (visValues.length > 0) {
      const avgVis = visValues.reduce((a, b) => a + b, 0) / visValues.length;
      // Fog is a topside hazard for diving, not an in-water quality penalty
      // (P7-15, audit 09 V2): it no longer dents the dive score, so the caution
      // surfaces here. Thresholds match the canonical divingSafetyIndicators fog
      // flag (red < 500m / amber < 1000m) and the coastal-family P7-5 convention;
      // clear/hazy reporting continues above 1km.
      indicators.push(
        avgVis < 500
          ? {
              label: "Dense fog",
              status: "poor",
              detail: `${(avgVis / 1000).toFixed(1)} km - boat navigation, the shotline and surfacing divers are hard to spot; operators may cancel.`,
            }
          : avgVis < 1000
            ? {
                label: "Reduced visibility",
                status: "moderate",
                detail: `${(avgVis / 1000).toFixed(1)} km - confirm boat cover and keep a clear surface reference.`,
              }
            : {
                label: "Surface visibility",
                status: avgVis > 5000 ? "good" : "moderate",
                detail:
                  avgVis > 5000
                    ? `${(avgVis / 1000).toFixed(0)} km - clear`
                    : `${(avgVis / 1000).toFixed(1)} km - hazy`,
              }
      );
    }

    return indicators;
  }, [hours, divingDetail]);

  if (hours.length === 0) return null;

  return (
    <div className="space-y-5">
      <h3 className="font-serif text-[18px] md:text-[22px] font-bold text-navy">
        Dive conditions detail
      </h3>

      {/* Always-visible: moon phase */}
      {divingDetail.moonPhase && (
        <div className="flex items-center gap-3 text-[13px]">
          <span className="text-text-muted">Moon</span>
          <span className="font-semibold text-navy">
            {divingDetail.moonPhase.tag.replace(/_/g, " ")} ({divingDetail.moonPhase.illuminationPct}%)
          </span>
        </div>
      )}

      {/* Always-visible: tide chart */}
      {tide && tide.points.length > 0 && (
        <TideChart tide={tide} tidePreference={tidePreference ?? null} />
      )}

      {explanation ? (
        <FactorFirst explanation={explanation} />
      ) : (
        <p className="rounded-[12px] border border-border-light bg-warm-bg p-4 text-[13px] text-text-muted">
          Factor explanation unavailable for this forecast.
        </p>
      )}

      <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
        <div className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-4">
          Underwater visibility / viz (Secchi depth)
        </div>
        <VisibilityGauge secchiDepthM={divingDetail.secchiDepthM ?? null} />
      </div>

      <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
        <div className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-4">
          Safety dashboard
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {safetyIndicators.map((ind) => (
            <SafetyCard key={ind.label} indicator={ind} />
          ))}
        </div>
      </div>

      {/* Collapsible: underwater conditions */}
      <CollapsibleSection title="Underwater conditions">
        <div className="space-y-4">
          <ThermoclineEstimate waterTempC={divingDetail.waterTempC ?? null} latitude={latitude ?? null} />
          <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-7">
            <WaterTemperatureCard
              waterTempC={divingDetail.waterTempC ?? null}
              wetsuitRecommendation={divingDetail.wetsuitRecommendation ?? null}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Collapsible: planning (boat, pressure, surge) */}
      {(enhancements?.surge || enhancements?.boatFeasibility || enhancements?.pressureTrend) && (
        <CollapsibleSection title="Planning">
          <div className="space-y-3">
            {enhancements?.surge && (
              <div className={`rounded-[10px] px-3.5 py-3 border ${
                enhancements.surge.level === "dangerous" ? "bg-red-50 border-red-200" :
                enhancements.surge.level === "strong" ? "bg-amber-50 border-amber-200" :
                "bg-warm-bg border-border-light"
              }`}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.8px] text-text-muted mb-0.5">Surge</div>
                <div className="text-[13px] font-semibold text-navy">{enhancements.surge.label}</div>
              </div>
            )}
            {enhancements?.boatFeasibility && (
              <div className={`rounded-[10px] px-3.5 py-3 border ${boatFeasibilityCardClass(enhancements.boatFeasibility.colour)}`}>
                <div className="text-[13px] font-semibold text-navy">{enhancements.boatFeasibility.label}</div>
              </div>
            )}
            {enhancements?.pressureTrend && (
              <div className="text-[12px] text-text-muted">{enhancements.pressureTrend.label}</div>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
